import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery } from "@apollo/client";
import { GET_ALL_GISTS, GET_ALL_SUBJECTS } from "../../services/graphql/queriesMutations";
import styles from "./NavigatorPage.module.css";

type NavigatorSubject = {
  id: number;
  title: string;
  parentId: number | null;
};

type NavigatorUser = {
  id?: number | null;
  name?: string | null;
  image?: string | null;
};

type NavigatorComment = {
  id: number;
  comment?: string | null;
  parentId?: number | null;
  isRemoved?: boolean | null;
  createdAt?: string | null;
  user?: NavigatorUser | null;
};

type NavigatorEdit = {
  id: number;
  body?: string | null;
  createdAt?: string | null;
  comments?: NavigatorComment[] | null;
  user?: NavigatorUser | null;
};

type NavigatorVersion = {
  id: number;
  point?: string | null;
  createdAt?: string | null;
  edits?: NavigatorEdit[] | null;
  user?: NavigatorUser | null;
};

type NavigatorGist = {
  id: number;
  title: string;
  parentId?: number | null;
  subjectId?: number | null;
  status?: string | null;
  publishedAt?: string | null;
  createdAt?: string | null;
  versions?: NavigatorVersion[] | null;
};

type SubjectsQuery = {
  subjects: NavigatorSubject[];
};

type GistsQuery = {
  gists: NavigatorGist[];
};

type SubjectCardNode = {
  id: string;
  subject: NavigatorSubject | null;
  label: string;
  subtitle: string;
  meta: string;
  x: number;
  y: number;
  selected?: boolean;
  pathNode?: boolean;
  childNode?: boolean;
  everything?: boolean;
};

type GistTreeNode = {
  gist: NavigatorGist;
  children: GistTreeNode[];
  depth: number;
  visibleDescendants: number;
  totalReplyCount: number;
};

type GistCardNode = GistTreeNode & {
  x: number;
  y: number;
};

type Connector = {
  id: string;
  fromX: number;
  fromY: number;
  toX: number;
  toY: number;
  hot?: boolean;
  type?: "subject" | "gist" | "subject-to-gist" | "comment";
};

type CommentMode = "hidden" | "markers" | "expanded";

type CommentMarkerNode = {
  comment: NavigatorComment;
  x: number;
  y: number;
  index: number;
};

type CommentThreadNode = {
  comment: NavigatorComment;
  children: CommentThreadNode[];
  depth: number;
  totalDescendants: number;
};

type CommentThreadCardNode = CommentThreadNode & {
  x: number;
  y: number;
  height: number;
  siblingIndex: number;
};

type CommentThreadLayoutResult = {
  nodes: CommentThreadCardNode[];
  links: Connector[];
  totalCandidateCount: number;
  totalVisibleCount: number;
};

type GistTreeResult = {
  roots: GistTreeNode[];
  rootCount: number;
  visibleCount: number;
  totalCandidateCount: number;
};

type GistLayoutResult = {
  nodes: GistCardNode[];
  links: Connector[];
};

const SUBJECT_WIDTH = 190;
const SUBJECT_HEIGHT = 82;
const SUBJECT_X_START = 80;
const SUBJECT_X_GAP = 245;
const SUBJECT_Y = 215;
const CHILD_X_GAP = 60;
const CHILD_Y_START = 92;
const CHILD_Y_GAP = 118;
const GIST_CARD_WIDTH = 335;
const GIST_CARD_HEIGHT = 220;
const GIST_COLUMN_GAP = 430;
const GIST_ROW_GAP = 258;
const GIST_TOP_Y = 92;
const COMMENT_MARKER_WIDTH = 320;
const COMMENT_MARKER_HEIGHT = 78;
const COMMENT_MARKER_GAP = 90;
const COMMENT_MARKER_TOP_GAP = 62;
const COMMENT_THREAD_CARD_WIDTH = 355;
const COMMENT_THREAD_CARD_HEIGHT = 200;
const COMMENT_THREAD_COLUMN_GAP = 420;
const COMMENT_THREAD_TOP_GAP = 82;
const COMMENT_THREAD_SIDE_GAP = 190;
const MAX_COMMENT_THREAD_ROOTS = 6;
const MAX_COMMENT_THREAD_DEPTH = 4;
const MAX_REPLIES_PER_COMMENT = 5;
const CANVAS_MIN_WIDTH = 1500;
const CANVAS_MIN_HEIGHT = 760;
const ZOOM_MIN = 0.65;
const ZOOM_MAX = 1.35;
const ZOOM_STEP = 0.1;
const MINI_MAP_WIDTH = 192;
const MINI_MAP_HEIGHT = 70;
const MAX_ROOT_GISTS = 10;
const MAX_GIST_DEPTH = 3;
const MAX_REPLIES_PER_GIST = 5;

function sortSubjects(subjects: NavigatorSubject[]): NavigatorSubject[] {
  return subjects.slice().sort((a, b) => a.title.localeCompare(b.title));
}

function buildSubjectMap(subjects: NavigatorSubject[]): { [id: number]: NavigatorSubject } {
  const byId: { [id: number]: NavigatorSubject } = {};
  subjects.forEach((subject) => {
    byId[subject.id] = subject;
  });
  return byId;
}

function buildSubjectBuckets(subjects: NavigatorSubject[]): { [parentKey: string]: NavigatorSubject[] } {
  const byId = buildSubjectMap(subjects);
  const buckets: { [parentKey: string]: NavigatorSubject[] } = {};

  subjects.forEach((subject) => {
    const hasKnownParent = typeof subject.parentId === "number" && Boolean(byId[subject.parentId]);
    const key = hasKnownParent ? String(subject.parentId) : "root";
    buckets[key] = buckets[key] || [];
    buckets[key].push(subject);
  });

  Object.keys(buckets).forEach((key) => {
    buckets[key] = sortSubjects(buckets[key]);
  });

  return buckets;
}

function getSubjectPath(subjects: NavigatorSubject[], subjectId: number | null): NavigatorSubject[] {
  if (!subjectId) return [];
  const byId = buildSubjectMap(subjects);
  const path: NavigatorSubject[] = [];
  let current: NavigatorSubject | undefined = byId[subjectId];
  let guard = 0;

  while (current && guard < 100) {
    path.unshift(current);
    current = current.parentId === null ? undefined : byId[current.parentId];
    guard += 1;
  }

  return path;
}

function getSubjectTitle(subjects: NavigatorSubject[], subjectId: number | null | undefined): string {
  if (!subjectId) return "No subject selected";
  return subjects.find((subject) => subject.id === subjectId)?.title || `Subject ${subjectId}`;
}

function countDescendants(subjectId: number, buckets: { [parentKey: string]: NavigatorSubject[] }): number {
  let total = 0;
  const visit = (parentId: number) => {
    (buckets[String(parentId)] || []).forEach((child) => {
      total += 1;
      visit(child.id);
    });
  };
  visit(subjectId);
  return total;
}

function getSubjectDepth(path: NavigatorSubject[]): number {
  return Math.max(0, path.length - 1);
}

function makeConnector(id: string, fromX: number, fromY: number, toX: number, toY: number, hot = false, type: Connector["type"] = "subject"): Connector {
  return { id, fromX, fromY, toX, toY, hot, type };
}

function makeConnectorPath(connector: Connector): string {
  const midX = Math.round((connector.fromX + connector.toX) / 2);
  return `M ${connector.fromX} ${connector.fromY} C ${midX} ${connector.fromY}, ${midX} ${connector.toY}, ${connector.toX} ${connector.toY}`;
}

function clampNumber(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function roundZoom(value: number): number {
  return Math.round(value * 100) / 100;
}

function formatSubjectMeta(subject: NavigatorSubject, buckets: { [parentKey: string]: NavigatorSubject[] }, path: NavigatorSubject[]): string {
  const childCount = (buckets[String(subject.id)] || []).length;
  const descendants = countDescendants(subject.id, buckets);
  const depth = getSubjectDepth(path);
  if (childCount === 0) return `Level ${depth} · no child Subjects`;
  return `Level ${depth} · ${childCount} children · ${descendants} descendants`;
}

function stripHtml(input?: string | null): string {
  if (!input) return "";
  if (typeof window !== "undefined" && typeof window.DOMParser !== "undefined") {
    const doc = new window.DOMParser().parseFromString(input, "text/html");
    return (doc.body.textContent || "").replace(/\s+/g, " ").trim();
  }
  return input.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
}

function excerpt(input?: string | null, limit = 180): string {
  const text = stripHtml(input);
  if (!text) return "No body text available in the latest edit.";
  if (text.length <= limit) return text;
  return `${text.slice(0, limit).trim()}…`;
}

function formatDate(date?: string | null): string {
  if (!date) return "unknown date";
  const parsed = new Date(date).getTime();
  if (Number.isNaN(parsed)) return "unknown date";
  return new Date(parsed).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

function getLatestVersion(gist: NavigatorGist | null): NavigatorVersion | null {
  if (!gist?.versions?.length) return null;
  return gist.versions
    .slice()
    .sort((a, b) => new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime())[0];
}

function getLatestEdit(gist: NavigatorGist | null): NavigatorEdit | null {
  const version = getLatestVersion(gist);
  if (!version?.edits?.length) return null;
  return version.edits
    .slice()
    .sort((a, b) => new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime())[0];
}

function getCommentCount(gist: NavigatorGist): number {
  return getGistComments(gist).length;
}

function getGistComments(gist: NavigatorGist | null): NavigatorComment[] {
  if (!gist) return [];
  const byId: { [id: number]: NavigatorComment } = {};

  (gist.versions || []).forEach((version) => {
    (version.edits || []).forEach((edit) => {
      (edit.comments || []).forEach((comment) => {
        if (!comment || comment.isRemoved || typeof comment.id !== "number") return;
        byId[comment.id] = comment;
      });
    });
  });

  return sortCommentsByNewest(Object.values(byId));
}

function sortCommentsByNewest(comments: NavigatorComment[]): NavigatorComment[] {
  return comments.slice().sort((a, b) => {
    const aTime = new Date(a.createdAt || 0).getTime();
    const bTime = new Date(b.createdAt || 0).getTime();
    return bTime - aTime;
  });
}

function sortCommentsByOldest(comments: NavigatorComment[]): NavigatorComment[] {
  return comments.slice().sort((a, b) => {
    const aTime = new Date(a.createdAt || 0).getTime();
    const bTime = new Date(b.createdAt || 0).getTime();
    return aTime - bTime;
  });
}

function getAuthorLabel(comment: NavigatorComment): string {
  return comment.user?.name || `User ${comment.user?.id || "unknown"}`;
}

function estimateCommentHeight(text: string | null | undefined, isExpanded: boolean): number {
  if (!isExpanded) return COMMENT_THREAD_CARD_HEIGHT;
  if (!text) return COMMENT_THREAD_CARD_HEIGHT;
  // Use a more generous estimation to avoid overflow
  // 355px width - 42px left margin - 20px right margin = ~293px text width
  // Approx 52 chars per line for 0.82rem Inter
  const charsPerLine = 52;
  const lines = Math.ceil(text.length / charsPerLine);
  // line-height is 1.38 * 0.82rem ~= 18.5px. Use 23px for safety including gaps.
  const bodyHeight = Math.max(70, lines * 23);
  // Header(52) + Footer(wrapped might be 52) + Padding(30) + Margin(13) ~= 147
  return bodyHeight + 150;
}

function buildCommentThread(comments: NavigatorComment[]): CommentThreadNode[] {
  const commentIds: { [id: number]: true } = {};
  comments.forEach((comment) => {
    commentIds[comment.id] = true;
  });

  const buckets: { [parentKey: string]: NavigatorComment[] } = {};
  comments.forEach((comment) => {
    const hasKnownParent = typeof comment.parentId === "number" && Boolean(commentIds[comment.parentId]);
    const key = hasKnownParent ? String(comment.parentId) : "root";
    buckets[key] = buckets[key] || [];
    buckets[key].push(comment);
  });

  Object.keys(buckets).forEach((key) => {
    buckets[key] = sortCommentsByOldest(buckets[key]);
  });

  const countDescendantsForComment = (commentId: number, seen: { [id: number]: true } = {}): number => {
    if (seen[commentId]) return 0;
    seen[commentId] = true;
    return (buckets[String(commentId)] || []).reduce(
      (sum, child) => sum + 1 + countDescendantsForComment(child.id, { ...seen } as { [id: number]: true }),
      0
    );
  };

  const buildNode = (comment: NavigatorComment, depth: number, seen: { [id: number]: true }): CommentThreadNode => {
    const nextSeen: { [id: number]: true } = { ...seen, [comment.id]: true };
    const allChildren = buckets[String(comment.id)] || [];
    const visibleChildren = depth >= MAX_COMMENT_THREAD_DEPTH
      ? []
      : allChildren.filter((child) => !nextSeen[child.id]).slice(0, MAX_REPLIES_PER_COMMENT);
    const children = visibleChildren.map((child) => buildNode(child, depth + 1, nextSeen));
    return {
      comment,
      children,
      depth,
      totalDescendants: countDescendantsForComment(comment.id),
    };
  };

  return (buckets.root || [])
    .slice(0, MAX_COMMENT_THREAD_ROOTS)
    .map((comment) => buildNode(comment, 0, {}));
}

function flattenCommentThread(nodes: CommentThreadNode[]): CommentThreadNode[] {
  const flat: CommentThreadNode[] = [];
  const visit = (node: CommentThreadNode) => {
    flat.push(node);
    node.children.forEach(visit);
  };
  nodes.forEach(visit);
  return flat;
}

function layoutCommentThread(
  gistNode: GistCardNode | null,
  comments: NavigatorComment[],
  mode: CommentMode,
  sideRail = false,
  expandedCommentId: number | null = null
): CommentThreadLayoutResult {
  if (!gistNode || mode !== "expanded") {
    return { nodes: [], links: [], totalCandidateCount: comments.length, totalVisibleCount: 0 };
  }

  const roots = buildCommentThread(comments);
  const nodes: CommentThreadCardNode[] = [];
  const links: Connector[] = [];
  const nextYByDepth: { [depth: number]: number } = {};
  const startX = sideRail ? gistNode.x + GIST_CARD_WIDTH + COMMENT_THREAD_SIDE_GAP : gistNode.x + 20;
  const startY = sideRail
    ? Math.max(GIST_TOP_Y + 34, gistNode.y + 4)
    : gistNode.y + GIST_CARD_HEIGHT + COMMENT_THREAD_TOP_GAP;
  const rootFromX = sideRail ? gistNode.x + GIST_CARD_WIDTH : gistNode.x + Math.round(GIST_CARD_WIDTH * 0.5);
  const rootFromY = sideRail ? gistNode.y + Math.round(GIST_CARD_HEIGHT * 0.45) : gistNode.y + GIST_CARD_HEIGHT;

  const takeNextY = (depth: number, height: number): number => {
    const y = nextYByDepth[depth] ?? startY;
    nextYByDepth[depth] = y + height + 36; // Dynamic height + gap
    return y;
  };

  const layoutNode = (node: CommentThreadNode, parent: CommentThreadCardNode | null, siblingIndex: number): CommentThreadCardNode => {
    const isExpanded = node.comment.id === expandedCommentId;
    const height = estimateCommentHeight(node.comment.comment, isExpanded);

    const positioned: CommentThreadCardNode = {
      ...node,
      x: startX + node.depth * COMMENT_THREAD_COLUMN_GAP,
      y: takeNextY(node.depth, height),
      height,
      siblingIndex,
    };

    nodes.push(positioned);

    if (parent) {
      links.push(
        makeConnector(
          `comment-${parent.comment.id}-${node.comment.id}`,
          parent.x + COMMENT_THREAD_CARD_WIDTH,
          parent.y + Math.round(parent.height * 0.46),
          positioned.x,
          positioned.y + Math.round(positioned.height * 0.46),
          node.depth <= 1,
          "comment"
        )
      );
    } else {
      links.push(
        makeConnector(
          `gist-${gistNode.gist.id}-comment-root-${node.comment.id}`,
          rootFromX,
          rootFromY,
          positioned.x,
          positioned.y + Math.round(positioned.height * 0.46),
          siblingIndex === 0,
          "comment"
        )
      );
    }

    node.children.forEach((child, childIndex) => layoutNode(child, positioned, childIndex));
    return positioned;
  };

  roots.forEach((root, index) => layoutNode(root, null, index));

  return {
    nodes,
    links,
    totalCandidateCount: comments.length,
    totalVisibleCount: flattenCommentThread(roots).length,
  };
}

function getCommentDepthLabel(depth: number): string {
  if (depth === 0) return "Root Comment";
  if (depth === 1) return "Reply · level 1";
  return `Reply · level ${depth}`;
}

function getCommentThreadNodeClass(depth: number): string {
  if (depth === 0) return styles.rootCommentNode;
  if (depth === 1) return styles.replyCommentNode;
  return styles.deepReplyCommentNode;
}

function getVisibleReplyCount(gistId: number, replyBuckets: { [parentId: number]: NavigatorGist[] }): number {
  let total = 0;
  const visit = (currentId: number) => {
    (replyBuckets[currentId] || []).forEach((child) => {
      total += 1;
      visit(child.id);
    });
  };
  visit(gistId);
  return total;
}

function sortGistsByNewest(gists: NavigatorGist[]): NavigatorGist[] {
  return gists.slice().sort((a, b) => {
    const aTime = new Date(a.publishedAt || a.createdAt || 0).getTime();
    const bTime = new Date(b.publishedAt || b.createdAt || 0).getTime();
    return bTime - aTime;
  });
}

function buildReplyBuckets(gists: NavigatorGist[]): { [parentId: number]: NavigatorGist[] } {
  const buckets: { [parentId: number]: NavigatorGist[] } = {};
  gists.forEach((gist) => {
    if (typeof gist.parentId !== "number") return;
    buckets[gist.parentId] = buckets[gist.parentId] || [];
    buckets[gist.parentId].push(gist);
  });

  Object.keys(buckets).forEach((key) => {
    const id = Number(key);
    buckets[id] = sortGistsByNewest(buckets[id]);
  });

  return buckets;
}

function getGistById(gists: NavigatorGist[], gistId: number | null): NavigatorGist | null {
  if (!gistId) return null;
  return gists.find((gist) => gist.id === gistId) || null;
}

function getGistAncestorIds(gists: NavigatorGist[], gistId: number | null): number[] {
  if (!gistId) return [];
  const byId: { [id: number]: NavigatorGist } = {};
  gists.forEach((gist) => {
    byId[gist.id] = gist;
  });

  const path: number[] = [];
  let current: NavigatorGist | undefined = byId[gistId];
  const seen: { [id: number]: true } = {};

  while (current && !seen[current.id]) {
    path.unshift(current.id);
    seen[current.id] = true;
    current = typeof current.parentId === "number" ? byId[current.parentId] : undefined;
  }

  return path;
}

function getGistDescendantIds(gists: NavigatorGist[], gistId: number | null): number[] {
  if (!gistId) return [];
  const buckets = buildReplyBuckets(gists);
  const descendants: number[] = [];

  const visit = (currentId: number, seen: { [id: number]: true }) => {
    if (seen[currentId]) return;
    const nextSeen: { [id: number]: true } = { ...seen, [currentId]: true };
    (buckets[currentId] || []).forEach((child) => {
      descendants.push(child.id);
      visit(child.id, nextSeen);
    });
  };

  visit(gistId, {});
  return descendants;
}

function makeNumberSet(ids: number[]): { [id: number]: true } {
  const set: { [id: number]: true } = {};
  ids.forEach((id) => {
    set[id] = true;
  });
  return set;
}

function isKeyboardActivation(event: React.KeyboardEvent): boolean {
  return event.key === "Enter" || event.key === " ";
}

function getRootGistsForSubject(gists: NavigatorGist[], subjectId: number | null): NavigatorGist[] {
  if (!subjectId) return [];
  const candidates = gists.filter((gist) => gist.subjectId === subjectId);
  const candidateIds: { [id: number]: true } = {};
  candidates.forEach((gist) => {
    candidateIds[gist.id] = true;
  });

  return sortGistsByNewest(
    candidates.filter((gist) => {
      if (typeof gist.parentId !== "number") return true;
      return !candidateIds[gist.parentId];
    })
  );
}

function buildGistTree(gists: NavigatorGist[], selectedSubjectId: number | null, focusGistId: number | null = null): GistTreeResult {
  if (!selectedSubjectId) {
    return { roots: [], rootCount: 0, visibleCount: 0, totalCandidateCount: 0 };
  }

  const replyBuckets = buildReplyBuckets(gists);
  const rootGists = getRootGistsForSubject(gists, selectedSubjectId);
  const focusPathIds = getGistAncestorIds(gists, focusGistId);
  const focusPathSet = makeNumberSet(focusPathIds);
  const focusedRoot = focusGistId
    ? rootGists.find((root) => Boolean(focusPathSet[root.id]) || root.id === focusGistId) || null
    : null;
  const visibleRoots = rootGists.slice(0, MAX_ROOT_GISTS);

  if (focusedRoot && !visibleRoots.some((root) => root.id === focusedRoot.id)) {
    visibleRoots.push(focusedRoot);
  }

  let visibleCount = 0;

  const buildNode = (gist: NavigatorGist, depth: number): GistTreeNode => {
    visibleCount += 1;
    const allChildren = replyBuckets[gist.id] || [];
    const regularChildren = depth >= MAX_GIST_DEPTH ? [] : allChildren.slice(0, MAX_REPLIES_PER_GIST);
    const focusedChildren = allChildren.filter((child) => Boolean(focusPathSet[child.id]));
    const childList: NavigatorGist[] = [];
    const seenChildren: { [id: number]: true } = {};

    regularChildren.concat(focusedChildren).forEach((child) => {
      if (seenChildren[child.id]) return;
      seenChildren[child.id] = true;
      childList.push(child);
    });

    const children = childList.map((child) => buildNode(child, depth + 1));
    const visibleDescendants = children.reduce((sum, child) => sum + 1 + child.visibleDescendants, 0);
    const totalReplyCount = getVisibleReplyCount(gist.id, replyBuckets);
    return { gist, children, depth, visibleDescendants, totalReplyCount };
  };

  return {
    roots: visibleRoots.map((gist) => buildNode(gist, 0)),
    rootCount: rootGists.length,
    visibleCount,
    totalCandidateCount: gists.filter((gist) => gist.subjectId === selectedSubjectId).length,
  };
}

function layoutGistTree(roots: GistTreeNode[], startX: number): GistLayoutResult {
  const nodes: GistCardNode[] = [];
  const links: Connector[] = [];
  const nextYByDepth: { [depth: number]: number } = {};

  const takeNextY = (depth: number): number => {
    const y = nextYByDepth[depth] ?? GIST_TOP_Y;
    nextYByDepth[depth] = y + GIST_ROW_GAP;
    return y;
  };

  const layoutNode = (node: GistTreeNode): GistCardNode => {
    const positioned: GistCardNode = {
      ...node,
      x: startX + node.depth * GIST_COLUMN_GAP,
      y: takeNextY(node.depth),
    };

    nodes.push(positioned);

    node.children.forEach((child) => {
      const childPosition = layoutNode(child);
      links.push(
        makeConnector(
          `gist-${positioned.gist.id}-${child.gist.id}`,
          positioned.x + GIST_CARD_WIDTH,
          positioned.y + Math.round(GIST_CARD_HEIGHT * 0.45),
          childPosition.x,
          childPosition.y + Math.round(GIST_CARD_HEIGHT * 0.45),
          positioned.depth === 0,
          "gist"
        )
      );
    });

    return positioned;
  };

  roots.forEach((root) => layoutNode(root));
  return { nodes, links };
}

function getDepthLabel(depth: number): string {
  if (depth === 0) return "Root Gist";
  if (depth === 1) return "Reply Gist · level 1";
  return `Reply Gist · level ${depth}`;
}
function parseNavigatorNumber(value: string | null): number | null {
  if (!value) return null;
  const parsed = Number.parseInt(value, 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

function wantsExpandedComments(value: string | null): boolean {
  if (!value) return false;
  return ["1", "true", "yes", "expanded"].includes(value.toLowerCase());
}


function getGistNodeClass(depth: number): string {
  if (depth === 0) return styles.rootGistNode;
  if (depth === 1) return styles.replyGistNode;
  return styles.deepReplyGistNode;
}

const NavigatorPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const { data: subjectData, loading: subjectsLoading, error: subjectsError } = useQuery<SubjectsQuery>(GET_ALL_SUBJECTS, {
    fetchPolicy: "cache-and-network",
  });
  const { data: gistData, loading: gistsLoading, error: gistsError } = useQuery<GistsQuery>(GET_ALL_GISTS, {
    fetchPolicy: "cache-and-network",
  });

  const subjects = useMemo(() => subjectData?.subjects || [], [subjectData]);
  const gists = useMemo(() => gistData?.gists || [], [gistData]);
  const buckets = useMemo(() => buildSubjectBuckets(subjects), [subjects]);
  const rootSubjects = useMemo(() => buckets.root || [], [buckets]);

  const [selectedSubjectId, setSelectedSubjectId] = useState<number | null>(null);
  const [selectedGistId, setSelectedGistId] = useState<number | null>(null);
  const [targetGistId, setTargetGistId] = useState<number | null>(null);
  const [openedGistId, setOpenedGistId] = useState<number | null>(null);
  const [selectedCommentId, setSelectedCommentId] = useState<number | null>(null);
  const [commentMode, setCommentMode] = useState<CommentMode>("hidden");
  const urlFocusAppliedRef = useRef<string | null>(null);
  const commentRouteScrollAppliedRef = useRef<string | null>(null);
  const [zoom, setZoom] = useState(1);
  const [zoomPopoverOpen, setZoomPopoverOpen] = useState(false);
  const [viewportSnapshot, setViewportSnapshot] = useState({ left: 0, top: 0, width: 1, height: 1, scrollWidth: 1, scrollHeight: 1 });
  const [isDragging, setIsDragging] = useState(false);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const dragStateRef = useRef({ startX: 0, startY: 0, startLeft: 0, startTop: 0 });

  useEffect(() => {
    if (selectedSubjectId !== null) return;
    // If there is an active focus route in the URL, do not auto-select a default subject yet.
    // Let the route-handler useEffect take priority once data is ready.
    if (searchParams.get("gistId") || searchParams.get("subjectId")) {
      return;
    }
    if (rootSubjects.length === 1) {
      setSelectedSubjectId(rootSubjects[0].id);
    }
  }, [rootSubjects, searchParams, selectedSubjectId]);

  useEffect(() => {
    const routeSubjectId = parseNavigatorNumber(searchParams.get("subjectId"));
    const routeGistId = parseNavigatorNumber(searchParams.get("gistId"));

    // A direct GIST route is an explicit Navigator focus request.
    // Do not reset it while the Navigator is still resolving the route subject,
    // especially when the URL subject is a broader entry subject and the GIST
    // itself belongs to a child subject.
    if (routeGistId !== null) {
      return;
    }

    if (routeSubjectId !== null && selectedSubjectId === routeSubjectId) {
      return;
    }

    setSelectedGistId(null);
    setTargetGistId(null);
    setOpenedGistId(null);
    setSelectedCommentId(null);
    setCommentMode("hidden");
  }, [searchParams, selectedSubjectId]);

  useEffect(() => {
    setSelectedCommentId(null);
  }, [selectedGistId]);

  useEffect(() => {
    const focusKey = searchParams.toString();
    const routeGistId = parseNavigatorNumber(searchParams.get("gistId"));
    const routeSubjectId = parseNavigatorNumber(searchParams.get("subjectId"));

    // Reset applied ref if URL becomes empty, ensuring we can re-apply focus if user returns
    if (urlFocusAppliedRef.current === focusKey) return;

    if (!focusKey) {
      urlFocusAppliedRef.current = "";
      return;
    }

    if (routeGistId === null && routeSubjectId === null) {
      urlFocusAppliedRef.current = focusKey;
      return;
    }

    // Wait for essential data to be available
    if (!subjects.length || !gists.length) return;

    const focusGist = routeGistId !== null ? gists.find((gist) => gist.id === routeGistId) || null : null;

    // If we have a gistId, we MUST wait for the gist object to be found in the loaded array
    if (routeGistId !== null && !focusGist) {
      return;
    }

    // Determine the target subject: prefer the gist's actual subjectId
    const nextSubjectId = typeof focusGist?.subjectId === "number" ? focusGist.subjectId : routeSubjectId;

    // Verify that the target subject actually exists in the current subject list
    if (nextSubjectId === null || !subjects.some((s) => s.id === nextSubjectId)) {
      return;
    }

    // Apply the focus state
    setSelectedSubjectId(nextSubjectId);

    if (focusGist) {
      setSelectedGistId(focusGist.id);
      setTargetGistId(focusGist.id);
      setOpenedGistId(null);
      setSelectedCommentId(null);
      const mode = wantsExpandedComments(searchParams.get("comments")) ? "expanded" : "hidden";
      setCommentMode(mode);
    } else {
      // Just a subject focus
      setSelectedGistId(null);
      setTargetGistId(null);
      setOpenedGistId(null);
      setSelectedCommentId(null);
      setCommentMode("hidden");
    }

    // Only mark as applied once all required state transitions have been triggered
    urlFocusAppliedRef.current = focusKey;
  }, [gists, searchParams, subjects]);

  const selectedPath = useMemo(() => getSubjectPath(subjects, selectedSubjectId), [subjects, selectedSubjectId]);
  const selectedSubject = selectedPath.length > 0 ? selectedPath[selectedPath.length - 1] : null;
  const nextSubjects = selectedSubject ? buckets[String(selectedSubject.id)] || [] : rootSubjects;
  const visibleChildSubjects = nextSubjects.slice(0, 18);
  const hiddenChildCount = Math.max(0, nextSubjects.length - visibleChildSubjects.length);

  const subjectNodes = useMemo<SubjectCardNode[]>(() => {
    const nodes: SubjectCardNode[] = [
      {
        id: "everything",
        subject: null,
        label: "Everything",
        subtitle: "Start of the Navigator map",
        meta: `${rootSubjects.length} root Subjects`,
        x: SUBJECT_X_START,
        y: SUBJECT_Y,
        selected: selectedSubjectId === null,
        pathNode: true,
        everything: true,
      },
    ];

    selectedPath.forEach((subject, index) => {
      const pathToSubject = selectedPath.slice(0, index + 1);
      nodes.push({
        id: `path-${subject.id}`,
        subject,
        label: subject.title,
        subtitle: index === selectedPath.length - 1 ? "Selected Subject" : "Path Subject",
        meta: formatSubjectMeta(subject, buckets, pathToSubject),
        x: SUBJECT_X_START + (index + 1) * SUBJECT_X_GAP,
        y: SUBJECT_Y,
        selected: index === selectedPath.length - 1,
        pathNode: true,
      });
    });

    const childX = SUBJECT_X_START + (selectedPath.length + 1) * SUBJECT_X_GAP + CHILD_X_GAP;
    visibleChildSubjects.forEach((subject, index) => {
      const row = index % 6;
      const column = Math.floor(index / 6);
      const pathToSubject = selectedSubject ? selectedPath.concat(subject) : [subject];
      nodes.push({
        id: `child-${subject.id}`,
        subject,
        label: subject.title,
        subtitle: selectedSubject ? "Child Subject" : "Root Subject",
        meta: formatSubjectMeta(subject, buckets, pathToSubject),
        x: childX + column * 250,
        y: CHILD_Y_START + row * CHILD_Y_GAP,
        childNode: true,
      });
    });

    return nodes;
  }, [buckets, rootSubjects.length, selectedPath, selectedSubject, selectedSubjectId, visibleChildSubjects]);

  const childColumnCount = Math.max(1, Math.ceil(visibleChildSubjects.length / 6));
  const gistStartX = SUBJECT_X_START + (selectedPath.length + 1) * SUBJECT_X_GAP + CHILD_X_GAP + childColumnCount * 250 + 30;
  const focusedRouteGistId = targetGistId ?? selectedGistId;
  const gistTree = useMemo(() => buildGistTree(gists, selectedSubjectId, focusedRouteGistId), [focusedRouteGistId, gists, selectedSubjectId]);
  const gistLayout = useMemo(() => layoutGistTree(gistTree.roots, gistStartX), [gistTree.roots, gistStartX]);
  const selectedGistNode = selectedGistId ? gistLayout.nodes.find((node) => node.gist.id === selectedGistId) || null : null;
  const selectedGist = selectedGistNode?.gist || null;
  const targetGistNode = targetGistId ? gistLayout.nodes.find((node) => node.gist.id === targetGistId) || null : null;
  const targetGist = getGistById(gists, targetGistId);
  const targetPathIds = useMemo(() => getGistAncestorIds(gists, targetGistId), [gists, targetGistId]);
  const targetDescendantIds = useMemo(() => getGistDescendantIds(gists, targetGistId), [gists, targetGistId]);
  const targetPathSet = useMemo(() => makeNumberSet(targetPathIds), [targetPathIds]);
  const targetDescendantSet = useMemo(() => makeNumberSet(targetDescendantIds), [targetDescendantIds]);
  const targetFocusActive = Boolean(targetGistId && targetGistNode);
  const selectedGistComments = useMemo(() => getGistComments(selectedGist), [selectedGist]);
  const openedGistNode = openedGistId ? gistLayout.nodes.find((node) => node.gist.id === openedGistId) || null : null;
  const openedGist = openedGistNode?.gist || null;
  const openedVersion = getLatestVersion(openedGist);
  const openedEdit = getLatestEdit(openedGist);
  const openedProductionLink = openedGist?.subjectId
    ? `/subject/${encodeURIComponent(getSubjectTitle(subjects, openedGist.subjectId))}/${openedGist.subjectId}?gistId=${openedGist.id}`
    : openedGist
      ? `/gist/${encodeURIComponent(openedGist.title)}/${openedGist.id}`
      : "/";
  const commentFocusActive = Boolean(targetFocusActive && selectedGistNode && selectedGistId === targetGistId && commentMode === "expanded");
  const commentMarkerNodes = useMemo<CommentMarkerNode[]>(() => [], []);
  const commentThreadLayout = useMemo(
    () => layoutCommentThread(selectedGistNode, selectedGistComments, commentMode, commentFocusActive, selectedCommentId),
    [commentFocusActive, commentMode, selectedGistComments, selectedGistNode, selectedCommentId]
  );
  const visibleCommentCount = commentMode === "expanded" ? commentThreadLayout.totalVisibleCount : commentMarkerNodes.length;
  const hiddenCommentCount = Math.max(0, selectedGistComments.length - visibleCommentCount);

  const connectors = useMemo<Connector[]>(() => {
    const links: Connector[] = [];
    const pathNodes = subjectNodes.filter((node) => node.pathNode);

    for (let index = 0; index < pathNodes.length - 1; index += 1) {
      const from = pathNodes[index];
      const to = pathNodes[index + 1];
      links.push(
        makeConnector(
          `${from.id}-${to.id}`,
          from.x + SUBJECT_WIDTH,
          from.y + Math.round(SUBJECT_HEIGHT / 2),
          to.x,
          to.y + Math.round(SUBJECT_HEIGHT / 2),
          true,
          "subject"
        )
      );
    }

    const anchor = pathNodes[pathNodes.length - 1];
    subjectNodes
      .filter((node) => node.childNode)
      .forEach((child) => {
        links.push(
          makeConnector(
            `${anchor.id}-${child.id}`,
            anchor.x + SUBJECT_WIDTH,
            anchor.y + Math.round(SUBJECT_HEIGHT / 2),
            child.x,
            child.y + Math.round(SUBJECT_HEIGHT / 2),
            child.y === CHILD_Y_START,
            "subject"
          )
        );
      });

    if (selectedSubject && gistLayout.nodes.length > 0) {
      const selectedSubjectNode = pathNodes[pathNodes.length - 1];
      gistTree.roots.forEach((root) => {
        const rootNode = gistLayout.nodes.find((node) => node.gist.id === root.gist.id);
        if (!rootNode) return;
        links.push(
          makeConnector(
            `subject-${selectedSubject.id}-gist-${root.gist.id}`,
            selectedSubjectNode.x + SUBJECT_WIDTH,
            selectedSubjectNode.y + Math.round(SUBJECT_HEIGHT / 2),
            rootNode.x,
            rootNode.y + Math.round(GIST_CARD_HEIGHT * 0.45),
            true,
            "subject-to-gist"
          )
        );
      });
    }

    if (selectedGistNode && commentMode === "markers" && commentMarkerNodes.length > 0) {
      commentMarkerNodes.forEach((marker) => {
        links.push(
          makeConnector(
            `gist-${selectedGistNode.gist.id}-comment-${marker.comment.id}`,
            selectedGistNode.x + Math.round(GIST_CARD_WIDTH * 0.5),
            selectedGistNode.y + GIST_CARD_HEIGHT,
            marker.x,
            marker.y + Math.round(COMMENT_MARKER_HEIGHT * 0.5),
            marker.index === 0,
            "comment"
          )
        );
      });
    }

    return links.concat(gistLayout.links, commentThreadLayout.links);
  }, [commentMarkerNodes, commentMode, commentThreadLayout.links, gistLayout.links, gistLayout.nodes, gistTree.roots, selectedGistNode, selectedSubject, subjectNodes]);

  const canvasWidth = useMemo(() => {
    const maxSubjectX = subjectNodes.length ? Math.max.apply(null, subjectNodes.map((node) => node.x)) : SUBJECT_X_START;
    const maxGistX = gistLayout.nodes.length ? Math.max.apply(null, gistLayout.nodes.map((node) => node.x)) : gistStartX;
    const maxMarkerX = commentMarkerNodes.length ? Math.max.apply(null, commentMarkerNodes.map((node) => node.x)) + COMMENT_MARKER_WIDTH : 0;
    const maxThreadX = commentThreadLayout.nodes.length ? Math.max.apply(null, commentThreadLayout.nodes.map((node) => node.x)) + COMMENT_THREAD_CARD_WIDTH : 0;
    return Math.max(CANVAS_MIN_WIDTH, maxSubjectX + SUBJECT_WIDTH + 260, maxGistX + GIST_CARD_WIDTH + 260, maxMarkerX + 260, maxThreadX + 260);
  }, [commentMarkerNodes, commentThreadLayout.nodes, gistLayout.nodes, gistStartX, subjectNodes]);

  const canvasHeight = useMemo(() => {
    const maxSubjectY = subjectNodes.length ? Math.max.apply(null, subjectNodes.map((node) => node.y)) : SUBJECT_Y;
    const maxGistY = gistLayout.nodes.length ? Math.max.apply(null, gistLayout.nodes.map((node) => node.y)) : GIST_TOP_Y;
    const maxMarkerY = commentMarkerNodes.length ? Math.max.apply(null, commentMarkerNodes.map((node) => node.y)) + COMMENT_MARKER_HEIGHT : 0;
    const maxThreadY = commentThreadLayout.nodes.length ? Math.max.apply(null, commentThreadLayout.nodes.map((node) => node.y)) + COMMENT_THREAD_CARD_HEIGHT : 0;
    return Math.max(CANVAS_MIN_HEIGHT, maxSubjectY + SUBJECT_HEIGHT + 190, maxGistY + GIST_CARD_HEIGHT + 190, maxMarkerY + 190, maxThreadY + 190);
  }, [commentMarkerNodes, commentThreadLayout.nodes, gistLayout.nodes, subjectNodes]);

  const scaledCanvasWidth = Math.ceil(canvasWidth * zoom);
  const scaledCanvasHeight = Math.ceil(canvasHeight * zoom);

  const selectedTitle = selectedSubject?.title || "Everything";
  const targetTitle = targetGist?.title || "No target";
  const selectedChildrenCount = selectedSubject ? (buckets[String(selectedSubject.id)] || []).length : rootSubjects.length;
  const selectedDescendantCount = selectedSubject ? countDescendants(selectedSubject.id, buckets) : subjects.length;
  const hiddenGistCount = Math.max(0, gistTree.totalCandidateCount - gistTree.visibleCount);
  const commentVisibilitySummary = commentMode === "expanded"
    ? `${commentThreadLayout.nodes.length} full thread nodes visible`
    : commentMode === "markers"
      ? `${commentMarkerNodes.length} compact markers visible`
      : "comments hidden";
  const expandedCommentNoticeTop = commentThreadLayout.nodes.length
    ? Math.max.apply(null, commentThreadLayout.nodes.map((node) => node.y + COMMENT_THREAD_CARD_HEIGHT + 18))
    : selectedGistNode
      ? selectedGistNode.y + GIST_CARD_HEIGHT + COMMENT_THREAD_TOP_GAP + 20
      : 0;
  const loading = subjectsLoading || gistsLoading;
  const error = subjectsError || gistsError;

  const updateViewportSnapshot = useCallback(() => {
    const scrollEl = scrollRef.current;
    if (!scrollEl) return;
    setViewportSnapshot({
      left: scrollEl.scrollLeft,
      top: scrollEl.scrollTop,
      width: scrollEl.clientWidth || 1,
      height: scrollEl.clientHeight || 1,
      scrollWidth: scrollEl.scrollWidth || 1,
      scrollHeight: scrollEl.scrollHeight || 1,
    });
  }, []);

  const handleCanvasMouseDown = (event: React.MouseEvent<HTMLDivElement>) => {
    if ((event.target as HTMLElement).closest("button, a, .navigator-clickable-card")) return;
    const scrollEl = scrollRef.current;
    if (!scrollEl) return;

    dragStateRef.current = {
      startX: event.clientX,
      startY: event.clientY,
      startLeft: scrollEl.scrollLeft,
      startTop: scrollEl.scrollTop,
    };
    setIsDragging(true);
    event.preventDefault();
  };

  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (event: MouseEvent) => {
      const scrollEl = scrollRef.current;
      if (!scrollEl) return;
      const state = dragStateRef.current;
      scrollEl.scrollLeft = state.startLeft - (event.clientX - state.startX);
      scrollEl.scrollTop = state.startTop - (event.clientY - state.startY);
      updateViewportSnapshot();
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDragging, updateViewportSnapshot]);

  const scrollToMapPoint = useCallback((x: number, y: number, offsetX = 180, offsetY = 140) => {
    const scrollEl = scrollRef.current;
    if (!scrollEl) return;
    scrollEl.scrollTo({
      left: Math.max(0, x * zoom - offsetX),
      top: Math.max(0, y * zoom - offsetY),
      behavior: "smooth",
    });
    window.setTimeout(updateViewportSnapshot, 220);
  }, [updateViewportSnapshot, zoom]);

  useEffect(() => {
    const focusKey = searchParams.toString();
    const routeGistId = parseNavigatorNumber(searchParams.get("gistId"));

    if (commentRouteScrollAppliedRef.current === focusKey) return;

    if (!focusKey) {
      commentRouteScrollAppliedRef.current = "";
      return;
    }

    if (routeGistId !== null && targetGistNode) {
      const routeWantsCommentFocus = wantsExpandedComments(searchParams.get("comments"));
      commentRouteScrollAppliedRef.current = focusKey;

      window.setTimeout(() => {
        if (routeWantsCommentFocus && commentMode === "expanded") {
          scrollToMapPoint(targetGistNode.x + GIST_CARD_WIDTH + COMMENT_THREAD_SIDE_GAP, targetGistNode.y, 110, 75);
        } else {
          scrollToMapPoint(targetGistNode.x, targetGistNode.y, 170, 80);
        }
      }, 250);
    }
  }, [commentMode, scrollToMapPoint, searchParams, targetGistNode, zoom]);

  const resetNavigator = () => {
    setSelectedSubjectId(null);
    setSelectedGistId(null);
    setTargetGistId(null);
    setOpenedGistId(null);
    setSelectedCommentId(null);
    setCommentMode("hidden");
    setZoom(1);
    setZoomPopoverOpen(false);
    scrollRef.current?.scrollTo({ left: 0, top: 0, behavior: "smooth" });
    window.setTimeout(updateViewportSnapshot, 220);
  };

  const focusPathEnd = () => {
    const pathEnd = subjectNodes.filter((node) => node.pathNode).slice(-1)[0];
    if (!pathEnd) return;
    scrollToMapPoint(pathEnd.x, pathEnd.y, 180, 170);
  };

  const focusGists = () => {
    scrollToMapPoint(gistStartX, GIST_TOP_Y, 180, 60);
  };

  const focusComments = () => {
    if (!selectedGistNode) return;
    if (commentFocusActive) {
      scrollToMapPoint(selectedGistNode.x + GIST_CARD_WIDTH + 42, selectedGistNode.y, 120, 70);
      return;
    }
    scrollToMapPoint(selectedGistNode.x, selectedGistNode.y + GIST_CARD_HEIGHT, 160, 70);
  };

  const focusTargetGist = () => {
    if (!targetGistNode) return;
    scrollToMapPoint(targetGistNode.x, targetGistNode.y, 170, 80);
  };

  const toggleTargetFocus = (gistId: number) => {
    setSelectedGistId(gistId);
    setSelectedCommentId(null);
    setTargetGistId((current) => (current === gistId ? null : gistId));
  };

  const activateCommentFocus = (gistId: number) => {
    setSelectedGistId(gistId);
    setTargetGistId(gistId);
    setSelectedCommentId(null);
    setCommentMode("expanded");
    window.setTimeout(() => {
      const node = gistLayout.nodes.find((candidate) => candidate.gist.id === gistId);
      if (!node) return;
      scrollToMapPoint(node.x, node.y, 160, 70);
    }, 80);
  };

  const zoomIn = () => {
    setZoom((current) => roundZoom(Math.min(ZOOM_MAX, current + ZOOM_STEP)));
  };

  const zoomOut = () => {
    setZoom((current) => roundZoom(Math.max(ZOOM_MIN, current - ZOOM_STEP)));
  };

  const resetZoom = () => {
    setZoom(1);
  };

  const exitFocusMode = () => {
    setTargetGistId(null);
    if (commentMode === "expanded") {
      setCommentMode("hidden");
    }
    setSelectedCommentId(null);
  };

  const cycleCommentMode = () => {
    if (commentMode === "expanded") {
      setTargetGistId(null);
      setCommentMode("hidden");
      return;
    }
    if (selectedGistId) {
      activateCommentFocus(selectedGistId);
      return;
    }
    setCommentMode("hidden");
  };

  const handleCanvasWheel = (event: React.WheelEvent<HTMLDivElement>) => {
    if (!event.ctrlKey && !event.metaKey) return;
    event.preventDefault();
    setZoom((current) => {
      const next = current + (event.deltaY < 0 ? ZOOM_STEP : -ZOOM_STEP);
      return roundZoom(clampNumber(next, ZOOM_MIN, ZOOM_MAX));
    });
  };

  useEffect(() => {
    window.requestAnimationFrame(updateViewportSnapshot);
  }, [canvasHeight, canvasWidth, scaledCanvasHeight, scaledCanvasWidth, updateViewportSnapshot]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      const tagName = target?.tagName?.toLowerCase();
      if (tagName === "input" || tagName === "textarea" || target?.isContentEditable) return;

      if (event.key === "+" || event.key === "=") {
        event.preventDefault();
        zoomIn();
      }
      if (event.key === "-" || event.key === "_") {
        event.preventDefault();
        zoomOut();
      }
      if (event.key === "0") {
        event.preventDefault();
        resetZoom();
      }
      if (event.key.toLowerCase() === "s") {
        event.preventDefault();
        focusPathEnd();
      }
      if (event.key.toLowerCase() === "g") {
        event.preventDefault();
        focusGists();
      }
      if (event.key.toLowerCase() === "c") {
        event.preventDefault();
        cycleCommentMode();
      }
      if (event.key.toLowerCase() === "t" && selectedGistId) {
        event.preventDefault();
        toggleTargetFocus(selectedGistId);
      }
      if (event.key === "Escape") {
        if (openedGistId) {
          event.preventDefault();
          setOpenedGistId(null);
          return;
        }
        if (zoomPopoverOpen) {
          event.preventDefault();
          setZoomPopoverOpen(false);
          return;
        }
        if (targetGistId) {
          event.preventDefault();
          exitFocusMode();
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  });

  const targetConnectorIds = useMemo(() => {
    const ids: { [id: string]: true } = {};
    if (!targetFocusActive || targetPathIds.length === 0) return ids;

    const pathNodes = subjectNodes.filter((node) => node.pathNode);
    for (let index = 0; index < pathNodes.length - 1; index += 1) {
      ids[`${pathNodes[index].id}-${pathNodes[index + 1].id}`] = true;
    }

    if (selectedSubject && targetPathIds.length > 0) {
      ids[`subject-${selectedSubject.id}-gist-${targetPathIds[0]}`] = true;
    }

    for (let index = 0; index < targetPathIds.length - 1; index += 1) {
      ids[`gist-${targetPathIds[index]}-${targetPathIds[index + 1]}`] = true;
    }

    return ids;
  }, [selectedSubject, subjectNodes, targetFocusActive, targetPathIds]);

  const miniViewportStyle = useMemo<React.CSSProperties>(() => {
    const leftRatio = viewportSnapshot.left / Math.max(1, viewportSnapshot.scrollWidth);
    const topRatio = viewportSnapshot.top / Math.max(1, viewportSnapshot.scrollHeight);
    const widthRatio = viewportSnapshot.width / Math.max(1, viewportSnapshot.scrollWidth);
    const heightRatio = viewportSnapshot.height / Math.max(1, viewportSnapshot.scrollHeight);
    const width = clampNumber(widthRatio * MINI_MAP_WIDTH, 20, MINI_MAP_WIDTH);
    const height = clampNumber(heightRatio * MINI_MAP_HEIGHT, 14, MINI_MAP_HEIGHT);
    return {
      left: clampNumber(leftRatio * MINI_MAP_WIDTH, 0, MINI_MAP_WIDTH - width),
      top: clampNumber(topRatio * MINI_MAP_HEIGHT, 0, MINI_MAP_HEIGHT - height),
      width,
      height,
    };
  }, [viewportSnapshot]);

  return (
    <main className={styles.page}>
      <header className={styles.topbar}>
        <div className={styles.identity}>
          <span className={styles.logoMark} aria-hidden="true">✦</span>
          <div>
            <p className={styles.kicker}>Gistoscope Labs</p>
            <h1>Navigator</h1>
            <p className={styles.subtitle}>Stage 13: Gist click opens a read-only preview; comments open only through View comments.</p>
          </div>
        </div>

        <div className={styles.searchBar}>
          <span aria-hidden="true">⌕</span>
          <span>Search the map...</span>
          <kbd>Ctrl K</kbd>
        </div>

        <div className={styles.toolbar} aria-label="Navigator controls">
          <button type="button" onClick={focusPathEnd}>Focus Subject</button>
          <button type="button" onClick={focusGists}>Focus Gists</button>
          <button type="button" onClick={focusComments}>Focus Comments</button>
          <button type="button" className={targetFocusActive ? styles.targetModeButtonActive : ""} onClick={focusTargetGist} disabled={!targetFocusActive}>Focus Target</button>
          <button type="button" className={targetFocusActive ? styles.exitTargetButton : ""} onClick={exitFocusMode} disabled={!targetFocusActive}>{commentFocusActive ? "Exit Comments" : "Exit Target"}</button>
          <button type="button" onClick={zoomOut}>−</button>
          <button type="button" onClick={resetZoom}>{Math.round(zoom * 100)}%</button>
          <button type="button" onClick={zoomIn}>+</button>
          <button
            type="button"
            className={commentMode === "expanded" ? styles.modeButtonActive : ""}
            onClick={() => {
              if (selectedGistId) {
                activateCommentFocus(selectedGistId);
              } else {
                setCommentMode("expanded");
              }
            }}
          >Comments: Expanded</button>
          <button type="button" className={commentMode === "hidden" ? styles.modeButtonActive : ""} onClick={() => setCommentMode("hidden")}>Comments: Hidden</button>
          <button type="button" onClick={resetNavigator}>Everything</button>
          <button type="button" className={styles.primaryAction}>Stage 13: gist preview</button>
        </div>
      </header>

      <section className={styles.workspace} aria-label="Navigator workspace">
        <aside className={styles.rail} aria-label="Navigator tools">
          <span className={styles.railActive}>⌖</span>
          <span>▱</span>
          <span>★</span>
          <span>◷</span>
          <span>⌬</span>
          <span>⚙</span>
        </aside>

        <div className={styles.canvasShell}>
          <div className={styles.statusPanel}>
            <span>Subjects: {subjects.length}</span>
            <span>Gists: {gists.length}</span>
            <span>Selected: {selectedTitle}</span>
            <span>Visible Gists: {gistTree.visibleCount}</span>
            <span>Selected comments: {selectedGistComments.length}</span>
            <span>Zoom: {Math.round(zoom * 100)}%</span>
            <span>Mode: {commentMode}</span>
            <span>Target: {targetFocusActive ? targetTitle : "off"}</span>
          </div>

          <div className={styles.canvasTools} aria-label="Canvas controls">
            <button type="button" onClick={focusPathEnd}>⌖</button>
            <button type="button" onClick={focusGists}>Gists</button>
            <button type="button" onClick={cycleCommentMode}>Comments</button>
            <button type="button" onClick={resetNavigator}>Reset</button>
            <button
              type="button"
              className={zoomPopoverOpen ? styles.zoomMenuButtonActive : ""}
              onClick={() => setZoomPopoverOpen((current) => !current)}
              aria-expanded={zoomPopoverOpen}
              aria-controls="navigator-zoom-popover"
            >
              {Math.round(zoom * 100)}%
            </button>
          </div>

          {zoomPopoverOpen && (
            <div id="navigator-zoom-popover" className={styles.zoomPopover} aria-label="Zoom and mini map controls">
              <div className={styles.zoomPopoverHeader}>
                <strong>Map tools</strong>
                <button type="button" onClick={() => setZoomPopoverOpen(false)} aria-label="Close map tools">×</button>
              </div>
              <div className={styles.zoomControlRow}>
                <button type="button" onClick={zoomIn} aria-label="Zoom in">+</button>
                <label className={styles.zoomSliderWrap}>
                  <span>{Math.round(zoom * 100)}%</span>
                  <input
                    className={styles.zoomSlider}
                    type="range"
                    min={ZOOM_MIN}
                    max={ZOOM_MAX}
                    step={ZOOM_STEP}
                    value={zoom}
                    onChange={(event) => setZoom(roundZoom(Number(event.currentTarget.value)))}
                    aria-label="Navigator zoom"
                  />
                </label>
                <button type="button" onClick={zoomOut} aria-label="Zoom out">−</button>
              </div>
              <button type="button" className={styles.zoomResetButton} onClick={resetZoom}>Reset zoom</button>
              <div className={styles.popoverMiniMap} aria-label="Navigator mini map preview">
                <div className={styles.miniMapHeader}>Mini map</div>
                <div className={styles.miniMapBody}>
                  <span className={styles.miniSubject} />
                  <span className={styles.miniSubject} />
                  <span className={styles.miniSubject} />
                  <span className={styles.miniGist} />
                  <span className={styles.miniComment} />
                  <span className={styles.miniViewport} style={miniViewportStyle} />
                </div>
              </div>
              <p className={styles.zoomPopoverHint}>Use this panel only when you need scale or position. The main map keeps the full screen.</p>
            </div>
          )}

          <div
            ref={scrollRef}
            className={`${styles.canvasViewport} ${isDragging ? styles.canvasViewportDragging : ""}`}
            onMouseDown={handleCanvasMouseDown}
            onScroll={updateViewportSnapshot}
            onWheel={handleCanvasWheel}
          >
            <div className={styles.scaledCanvasStage} style={{ width: scaledCanvasWidth, height: scaledCanvasHeight }}>
            <div className={`${styles.canvas} ${targetFocusActive ? styles.targetFocusCanvas : ""}`} style={{ width: canvasWidth, height: canvasHeight, transform: `scale(${zoom})` }}>
              <svg className={styles.connectors} width={canvasWidth} height={canvasHeight} viewBox={`0 0 ${canvasWidth} ${canvasHeight}`} aria-hidden="true">
                {connectors.map((connector) => (
                  <path
                    key={connector.id}
                    className={`${connector.hot ? styles.hotConnector : styles.connector} ${connector.type === "gist" ? styles.gistConnector : ""} ${connector.type === "subject-to-gist" ? styles.subjectToGistConnector : ""} ${connector.type === "comment" ? styles.commentConnector : ""} ${targetConnectorIds[connector.id] ? styles.targetConnector : ""} ${targetFocusActive && !targetConnectorIds[connector.id] && !(commentFocusActive && connector.type === "comment") ? styles.targetFadedConnector : ""}`}
                    d={makeConnectorPath(connector)}
                    fill="none"
                  />
                ))}
                {connectors.map((connector) => (
                  <circle
                    key={`${connector.id}-dot`}
                    className={`${connector.hot ? styles.hotDot : styles.dot} ${targetConnectorIds[connector.id] ? styles.targetDot : ""} ${targetFocusActive && !targetConnectorIds[connector.id] && !(commentFocusActive && connector.type === "comment") ? styles.targetFadedDot : ""}`}
                    cx={connector.fromX}
                    cy={connector.fromY}
                    r="5"
                  />
                ))}
              </svg>

              {subjectNodes.map((node) => {
                const clickHandler = node.everything ? () => setSelectedSubjectId(null) : () => node.subject && setSelectedSubjectId(node.subject.id);
                return (
                  <button
                    type="button"
                    key={node.id}
                    className={`${styles.subjectNode} ${node.selected ? styles.selectedSubject : ""} ${node.childNode ? styles.childSubjectNode : ""} ${targetFocusActive && node.pathNode ? styles.targetSubjectRopeNode : ""} ${targetFocusActive && node.childNode ? styles.targetGhostSubjectNode : ""} navigator-clickable-card`}
                    style={{ left: node.x, top: node.y }}
                    onClick={clickHandler}
                    title={node.everything ? "Reset to Everything" : `Open ${node.label}`}
                  >
                    <span className={styles.subjectIcon} aria-hidden="true">⌬</span>
                    <span className={styles.subjectText}>
                      <strong>{node.label}</strong>
                      <em>{node.subtitle}</em>
                      <small>{node.meta}</small>
                    </span>
                  </button>
                );
              })}

              {gistLayout.nodes.map((node) => {
                const latestEdit = getLatestEdit(node.gist);
                const commentCount = getCommentCount(node.gist);
                const isSelected = selectedGistId === node.gist.id;
                const isTargeted = targetGistId === node.gist.id;
                const isInTargetPath = Boolean(targetPathSet[node.gist.id]);
                const isTargetDescendant = Boolean(targetDescendantSet[node.gist.id]);
                const staysOpenInFocus = commentFocusActive ? isInTargetPath : isInTargetPath || isTargetDescendant;
                const isCollapsedByTarget = targetFocusActive && !staysOpenInFocus;

                if (isCollapsedByTarget) {
                  return (
                    <button
                      type="button"
                      key={`gist-${node.gist.id}`}
                      className={`${styles.targetGhostGistNode} navigator-clickable-card`}
                      style={{ left: node.x, top: node.y }}
                      onClick={() => {
                        setSelectedGistId(node.gist.id);
                        setOpenedGistId(node.gist.id);
                      }}
                      title={`Open preview for side Gist compressed by Focus Mode: ${node.gist.title}`}
                    >
                      <span className={styles.targetGhostStripe} aria-hidden="true" />
                      <span className={styles.targetGhostDot} aria-hidden="true" />
                      <span className={styles.targetGhostMeta}>{getDepthLabel(node.depth)}</span>
                    </button>
                  );
                }

                return (
                  <div
                    role="button"
                    tabIndex={0}
                    key={`gist-${node.gist.id}`}
                    className={`${styles.gistNode} ${getGistNodeClass(node.depth)} ${isSelected ? styles.selectedGistNode : ""} ${isInTargetPath ? styles.targetPathGistNode : ""} ${isTargetDescendant ? styles.targetDescendantGistNode : ""} ${isTargeted ? styles.targetedGistNode : ""} ${commentFocusActive && isTargeted ? styles.commentFocusedGistNode : ""} navigator-clickable-card`}
                    style={{ left: node.x, top: node.y }}
                    onClick={() => {
                      setSelectedGistId(node.gist.id);
                      setCommentMode("hidden");
                      setOpenedGistId(node.gist.id);
                    }}
                    onKeyDown={(event) => {
                      if (!isKeyboardActivation(event)) return;
                      event.preventDefault();
                      setSelectedGistId(node.gist.id);
                      setCommentMode("hidden");
                      setOpenedGistId(node.gist.id);
                    }}
                    title={`Open Gist preview: ${node.gist.title}`}
                  >
                    <div className={styles.gistCardHeader}>
                      <span className={styles.gistIcon}>G</span>
                      <span>{getDepthLabel(node.depth)}</span>
                      <time>{formatDate(node.gist.publishedAt || node.gist.createdAt)}</time>
                      <button
                        type="button"
                        className={`${styles.targetButton} ${isTargeted ? styles.targetButtonActive : ""}`}
                        aria-pressed={isTargeted}
                        title={isTargeted ? "Exit Target Focus for this thread" : "Focus this thread"}
                        onClick={(event) => {
                          event.stopPropagation();
                          toggleTargetFocus(node.gist.id);
                        }}
                      >
                        <span className={styles.targetBullseye} aria-hidden="true" />
                        <span className={styles.targetButtonLabel}>{isTargeted ? "Exit Target Focus" : "Target"}</span>
                      </button>
                    </div>
                    <div className={styles.gistCardBody}>
                      <h2>{node.gist.title}</h2>
                      <p>{excerpt(latestEdit?.body)}</p>
                    </div>
                    <footer className={styles.gistCardFooter}>
                      <span className={styles.gistBadge}>{node.gist.status || "Gist"}</span>
                      <span>{node.totalReplyCount} replies</span>
                      <button
                        type="button"
                        className={styles.commentFocusButton}
                        title={commentCount > 0 ? "Open comments as a right-side Navigator thread" : "No comments attached to this Gist yet"}
                        disabled={commentCount === 0}
                        onClick={(event) => {
                          event.stopPropagation();
                          if (commentFocusActive && isTargeted) {
                            exitFocusMode();
                          } else if (commentCount > 0) {
                            activateCommentFocus(node.gist.id);
                          }
                        }}
                      >☵ {commentFocusActive && isTargeted ? "Exit Comment Focus" : commentCount > 0 ? `View ${commentCount} ${commentCount === 1 ? "comment" : "comments"}` : "0 comments"}</button>
                      {isInTargetPath && <span className={styles.targetPathBadge}>Target path</span>}
                      {isTargetDescendant && !isInTargetPath && !commentFocusActive && <span className={styles.targetDownstreamBadge}>Downstream</span>}
                    </footer>
                  </div>
                );
              })}

              {commentMarkerNodes.map((node) => (
                <button
                  type="button"
                  key={`comment-marker-${node.comment.id}`}
                  className={`${styles.commentMarkerNode} navigator-clickable-card`}
                  style={{ left: node.x, top: node.y }}
                  title="Expand this Gist's Comment thread"
                  onClick={() => {
                    if (selectedGistNode) activateCommentFocus(selectedGistNode.gist.id);
                    setSelectedCommentId(node.comment.id);
                  }}
                >
                  <div className={styles.commentMarkerHeader}>
                    <span className={styles.commentNodeIcon} aria-hidden="true">☵</span>
                    <strong>{getAuthorLabel(node.comment)}</strong>
                    <time>{formatDate(node.comment.createdAt)}</time>
                  </div>
                  <p>{excerpt(node.comment.comment, 112)}</p>
                  <footer>Click to expand full thread</footer>
                </button>
              ))}

              {commentThreadLayout.nodes.map((node) => {
                const isExpanded = selectedCommentId === node.comment.id;
                return (
                  <button
                    type="button"
                    key={`comment-thread-${node.comment.id}`}
                    className={`${styles.commentThreadNode} ${getCommentThreadNodeClass(node.depth)} ${isExpanded ? styles.selectedCommentNodeExpanded : ""} navigator-clickable-card`}
                    style={{ left: node.x, top: node.y, height: node.height }}
                    onClick={() => setSelectedCommentId(isExpanded ? null : node.comment.id)}
                    title={isExpanded ? "Click to collapse" : `Click to expand comment by ${getAuthorLabel(node.comment)}`}
                  >
                    <div className={styles.commentThreadHeader}>
                      <span className={styles.commentNodeIcon} aria-hidden="true">☵</span>
                      <strong>{getAuthorLabel(node.comment)}</strong>
                      <time>{formatDate(node.comment.createdAt)}</time>
                    </div>
                    <p>{isExpanded ? node.comment.comment : excerpt(node.comment.comment, 172)}</p>
                    <footer className={styles.commentThreadFooter}>
                      <span>{getCommentDepthLabel(node.depth)}</span>
                      <span>{node.children.length} direct replies</span>
                      <span>{node.totalDescendants} total replies</span>
                      {isExpanded && <span className={styles.expandedHint}>Click to collapse</span>}
                    </footer>
                  </button>
                );
              })}

              {selectedGistNode && selectedGistComments.length > 0 && commentMode === "hidden" && (
                <aside
                  className={styles.commentsHiddenPanel}
                  style={{ left: selectedGistNode.x + 12, top: selectedGistNode.y + GIST_CARD_HEIGHT + 62 }}
                >
                  <strong>{selectedGistComments.length} comments hidden</strong>
                  <button type="button" onClick={() => selectedGistNode && activateCommentFocus(selectedGistNode.gist.id)}>Expand thread</button>
                </aside>
              )}

              {selectedGistNode && selectedGistComments.length === 0 && (
                <aside
                  className={styles.noCommentsPanel}
                  style={{ left: selectedGistNode.x + 12, top: selectedGistNode.y + GIST_CARD_HEIGHT + 62 }}
                >
                  No comments attached to the selected Gist yet.
                </aside>
              )}

              {selectedSubject && gistLayout.nodes.length === 0 && !loading && !error && (
                <section className={styles.emptyGistPanel} style={{ left: gistStartX, top: 390 }}>
                  <div className={styles.gistPlaceholderHeader}>
                    <span className={styles.gistIcon}>G</span>
                    <span>No Gists in this Subject yet</span>
                  </div>
                  <h2>“{selectedSubject.title}” has no direct Gist branch to show here.</h2>
                  <p>
                    You can still move deeper through child Subjects on the left side. Navigator shows Gists attached directly to the selected Subject and can expand a selected Gist into a right-side Comment Lab thread.
                  </p>
                  <footer>
                    {selectedChildrenCount} child Subjects · {selectedDescendantCount} descendant Subjects
                  </footer>
                </section>
              )}

              {!selectedSubject && (
                <section className={styles.emptyGistPanel} style={{ left: 640, top: 430 }}>
                  <div className={styles.gistPlaceholderHeader}>
                    <span className={styles.gistIcon}>G</span>
                    <span>Choose a Subject</span>
                  </div>
                  <h2>Select a Subject path to unlock the Gist layer</h2>
                  <p>
                    Start from Everything, then choose a root Subject. Navigator will keep the Subject path on the left and render real Gists on the right.
                  </p>
                  <footer>{subjects.length} total Subjects · {gists.length} total Gists</footer>
                </section>
              )}

              {selectedGist && (
                <aside className={styles.selectedGistPanel} style={{ left: Math.max(44, gistStartX), top: Math.max(44, canvasHeight - 150) }}>
                  <strong>Selected Gist</strong>
                  <span>{selectedGist.title}</span>
                  <small>{selectedGistComments.length} comments · {commentVisibilitySummary}. Click the View comments pill to open the right-side comment branch.</small>
                </aside>
              )}


              {hiddenChildCount > 0 && (
                <div className={styles.moreChildrenNotice} style={{ left: Math.max(760, canvasWidth - 360), top: 100 }}>
                  +{hiddenChildCount} more Subjects hidden in this safety preview. Navigator keeps the visible fan-out readable.
                </div>
              )}

              {hiddenGistCount > 0 && (
                <div className={styles.moreGistsNotice} style={{ left: Math.max(760, canvasWidth - 380), top: 188 }}>
                  +{hiddenGistCount} more Gists/replies are hidden by Navigator safety limits. This keeps Navigator usable while the graph engine is still evolving.
                </div>
              )}

              {hiddenCommentCount > 0 && commentMode === "markers" && selectedGistNode && (
                <div className={styles.moreCommentsNotice} style={{ left: selectedGistNode.x + 12, top: selectedGistNode.y + GIST_CARD_HEIGHT + COMMENT_MARKER_TOP_GAP + 4 * COMMENT_MARKER_GAP + 14 }}>
                  +{hiddenCommentCount} more comments hidden by the compact marker limit. Use Comments: Expanded to read the full visible thread.
                </div>
              )}

              {hiddenCommentCount > 0 && commentMode === "expanded" && selectedGistNode && (
                <div className={styles.moreCommentsNotice} style={{ left: commentFocusActive ? selectedGistNode.x + GIST_CARD_WIDTH + COMMENT_THREAD_SIDE_GAP : selectedGistNode.x + 12, top: expandedCommentNoticeTop }}>
                  +{hiddenCommentCount} more comments are hidden by Navigator safety limits. Comment Focus keeps the same Navigator background while showing the real comment hierarchy.
                </div>
              )}

              {loading && <div className={styles.loadingBlock}>Loading real Navigator data…</div>}
              {error && <div className={styles.errorBlock}>Could not load Navigator data: {error.message}</div>}
              {!loading && !error && subjects.length === 0 && (
                <div className={styles.emptyBlock}>No Subjects returned by the existing GET_ALL_SUBJECTS query.</div>
              )}
            </div>
            </div>
          </div>

        </div>
      </section>

      <section className={styles.legend} aria-label="Navigator visual language">
        <article className={styles.legendPanel}>
          <div className={styles.legendSubjectCard}>
            <span className={styles.subjectIcon}>⌬</span>
            <strong>Subject</strong>
          </div>
          <div className={styles.legendCopy}>
            <h3>Subject</h3>
            <p>Structural map category. Calm, geometric, minimal.</p>
          </div>
        </article>
        <article className={styles.legendPanel}>
          <div className={styles.legendGistCard}>
            <div className={styles.gistPlaceholderHeader}><span className={styles.gistIcon}>G</span><span>Gist</span></div>
            <strong>Main content unit</strong>
            <p>Title, excerpt, metadata.</p>
          </div>
          <div className={styles.legendCopy}>
            <h3>Gist</h3>
            <p>Primary content object. Bigger, denser, stronger header.</p>
          </div>
        </article>
        <article className={styles.legendPanel}>
          <div className={styles.legendCommentCard}>
            <span className={styles.commentIcon}>☵</span>
            <strong>Comment</strong>
            <p>Conversation and replies.</p>
          </div>
          <div className={styles.legendCopy}>
            <h3>Comment</h3>
            <p>Light conversational card. Smaller and thread-oriented.</p>
          </div>
        </article>
      </section>
      {openedGist && openedGistNode && (
        <section className={styles.gistPreviewOverlay} onClick={() => setOpenedGistId(null)} role="presentation">
          <article className={styles.gistPreviewModal} onClick={(event) => event.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby="navigator-gist-preview-title">
            <button type="button" className={styles.gistPreviewCloseButton} onClick={() => setOpenedGistId(null)}>
              Close
            </button>
            <p className={styles.gistPreviewEyebrow}>Read-only Gist preview · {getDepthLabel(openedGistNode.depth)}</p>
            <h2 id="navigator-gist-preview-title">{openedGist.title}</h2>
            <p className={styles.gistPreviewPoint}>{stripHtml(openedVersion?.point) || "No summary available."}</p>
            <div className={styles.gistPreviewBody}>{stripHtml(openedEdit?.body) || "No body available in the latest edit."}</div>
            <div className={styles.gistPreviewFooter}>
              <span>{openedGistNode.children.length} direct Reply Gists</span>
              <span>{openedGistNode.totalReplyCount} total descendant Reply Gists</span>
              <button
                type="button"
                className={styles.gistPreviewPillButton}
                disabled={getCommentCount(openedGist) === 0}
                onClick={() => {
                  if (getCommentCount(openedGist) === 0) return;
                  setOpenedGistId(null);
                  activateCommentFocus(openedGist.id);
                }}
              >
                {getCommentCount(openedGist) > 0 ? `View ${getCommentCount(openedGist)} ${getCommentCount(openedGist) === 1 ? "comment" : "comments"}` : "0 comments"}
              </button>
              <span>Subject: {getSubjectTitle(subjects, openedGist.subjectId)}</span>
              <Link to={openedProductionLink} className={styles.gistPreviewPrimaryLink}>
                Open production Gist route
              </Link>
              <Link to={`/comment-lab?gistId=${openedGist.id}`} className={styles.gistPreviewPrimaryLink}>
                Open Comment Lab
              </Link>
            </div>
          </article>
        </section>
      )}

    </main>
  );
};

export default NavigatorPage;
